import { asRecord } from '@payaca/utilities/objectUtilities';
import { z } from 'zod/v4';

/**
 * Account-level EESS (battery) settings for the solar estimate document.
 *
 * MCS 032:2025 §4.4.1 leaves it to the installer how much of a battery's
 * usable capacity is set aside for self-consumption; the rest is reported
 * under "Additional benefits" (§4.3). The text fields are items of
 * MIS 3012 §4 / MCS 032 §6.3.2 that only the installer can answer: p) the
 * end-of-life arrangements, q) the terms of any third-party control. Null
 * prints as "to be confirmed" on the document, except p), which prints the
 * default wording.
 */
export const solarEessSettingsSchema = z.object({
  selfConsumptionAllocationPercent: z.number().min(0).max(100),
  thirdPartyControlTerms: z.string().trim().min(1).max(2000).nullable(),
  endOfLifeArrangements: z.string().trim().min(1).max(2000).nullable(),
});

export type SolarEessSettings = z.infer<typeof solarEessSettingsSchema>;

export const DEFAULT_EESS_END_OF_LIFE_ARRANGEMENTS =
  'We will take the battery back at the end of its life and arrange its recycling in accordance with the Waste Electrical and Electronic Equipment Regulations 2013 as amended and the Batteries and Accumulators (Placing on the Market) Regulations 2008 as amended.';

export const DEFAULT_SOLAR_EESS_SETTINGS: SolarEessSettings = {
  selfConsumptionAllocationPercent: 100,
  thirdPartyControlTerms: null,
  endOfLifeArrangements: DEFAULT_EESS_END_OF_LIFE_ARRANGEMENTS,
};

/**
 * Every field that validates is kept; the rest fall back to the default, so
 * one bad value never discards the installer's other answers. An empty
 * string reads as "not set".
 */
export const parseSolarEessSettings = (value: unknown): SolarEessSettings => {
  const source = asRecord(value);
  const field = <K extends keyof SolarEessSettings>(
    key: K
  ): SolarEessSettings[K] => {
    const raw = source[key] === '' ? null : source[key];
    const parsed = solarEessSettingsSchema.shape[key].safeParse(raw);
    return parsed.success
      ? (parsed.data as SolarEessSettings[K])
      : DEFAULT_SOLAR_EESS_SETTINGS[key];
  };
  return {
    selfConsumptionAllocationPercent: field('selfConsumptionAllocationPercent'),
    thirdPartyControlTerms: field('thirdPartyControlTerms'),
    endOfLifeArrangements: field('endOfLifeArrangements'),
  };
};
